<template>
  <vl-map :data-projection="projectionName" :load-tiles-while-animating="true" :load-tiles-while-interacting="true"
    :keyboard-event-target="document" @pointermove="$emit('pointermove', $event.coordinate)"
    @mounted="$emit('map-mounted')" ref="map">
    <vl-view :center.sync="syncedCenter" :zoom.sync="syncedZoom" :rotation.sync="syncedRotation" :max-zoom="maxZoom"
      :max-resolution="Math.pow(2, image.zoom)" :extent="extent" :projection="projectionName"
      @mounted="$emit('view-mounted')" ref="view" />
    <vl-layer-tile :extent="extent" @mounted="$emit('base-layer-mounted')" ref="baseLayer">
      <vl-source-cytomine :projection="projectionName" :url="baseLayerUrl" :tile-load-function="tileLoadFunction"
        :size="imageSize" :extent="extent" :nb-resolutions="image.zoom" :transition="0"
        :tile-size="[tileSize, tileSize]" />
    </vl-layer-tile>
    <heatmap-overlay-layer v-if="heatmapOverlay && heatmapOverlay.url" :index="index" />
    <malaria-heatmap-layer
      v-for="layer in malariaHeatmapLayers"
      :key="layer.id"
      :id="layer.id"
      :visible="layer.visible"
      :opacity="layer.opacity"
      :z-index="layer.zIndex"
      :extent="extent"
      :points="layer.points"
      :radius="layer.radius"
      :blur="layer.blur"
      :gradient="layer.gradient"
    />
    <malaria-detection-overlay-layer
      v-if="malariaDetectionOverlay && malariaDetectionOverlay.visible && malariaDetectionOverlayItems.length"
      :id="`malaria-detections-${index}`"
      :visible="malariaDetectionOverlay.visible"
      :opacity="malariaDetectionOverlay.opacity"
      :z-index="malariaDetectionOverlay.zIndex"
      :extent="extent"
      :annotations="malariaDetectionOverlayItems"
      :points="malariaDetectionOverlay.points"
    />
    <annotation-layer v-for="layer in selectedLayers" :key="'layer-' + layer.id" :index="index" :layer="layer" />
    <select-interaction v-if="activeSelectInteraction" :index="index" />
    <draw-interaction v-if="activeDrawInteraction" :index="index" />
    <modify-interaction v-if="activeModifyInteraction" :index="index" />
  </vl-map>
</template>

<script>
import AnnotationLayer from './AnnotationLayer';
import HeatmapOverlayLayer from './HeatmapOverlayLayer';
import MalariaDetectionOverlayLayer from './MalariaDetectionOverlayLayer';
import MalariaHeatmapLayer from './MalariaHeatmapLayer';
import SelectInteraction from './interactions/SelectInteraction';
import DrawInteraction from './interactions/DrawInteraction';
import ModifyInteraction from './interactions/ModifyInteraction';

export default {
  name: 'viewer-image-map',
  components: {
    AnnotationLayer,
    HeatmapOverlayLayer,
    MalariaDetectionOverlayLayer,
    MalariaHeatmapLayer,
    SelectInteraction,
    DrawInteraction,
    ModifyInteraction
  },
  props: {
    activeDrawInteraction: Boolean,
    activeModifyInteraction: Boolean,
    activeSelectInteraction: Boolean,
    baseLayerUrl: {
      type: String,
      required: true
    },
    center: {
      type: Array,
      required: true
    },
    document: {
      required: true
    },
    extent: {
      type: Array,
      required: true
    },
    heatmapOverlay: Object,
    malariaDetectionOverlay: Object,
    malariaHeatmap: Object,
    image: {
      type: Object,
      required: true
    },
    imageSize: {
      type: Array,
      required: true
    },
    index: {
      type: String,
      required: true
    },
    maxZoom: {
      type: Number,
      required: true
    },
    projectionName: {
      type: String,
      required: true
    },
    rotation: {
      type: Number,
      required: true
    },
    selectedLayers: {
      type: Array,
      default: () => []
    },
    tileLoadFunction: {
      type: Function,
      required: true
    },
    tileSize: {
      type: Number,
      required: true
    },
    zoom: {
      type: Number,
      required: true
    }
  },
  computed: {
    syncedCenter: {
      get() {
        return this.center;
      },
      set(center) {
        this.$emit('update:center', center);
      }
    },
    syncedZoom: {
      get() {
        return this.zoom;
      },
      set(zoom) {
        this.$emit('update:zoom', zoom);
      }
    },
    syncedRotation: {
      get() {
        return this.rotation;
      },
      set(rotation) {
        this.$emit('update:rotation', rotation);
      }
    },
    malariaHeatmapLayers() {
      if (!this.malariaHeatmap || !this.malariaHeatmap.visible) {
        return [];
      }
      if (Array.isArray(this.malariaHeatmap.layers)) {
        return this.malariaHeatmap.layers
          .filter(layer => layer.visible !== false && layer.points && layer.points.length)
          .map((layer, layerIndex) => ({
            id: layer.id || `malaria-heatmap-${this.index}-${layer.type || layerIndex}`,
            visible: true,
            opacity: layer.opacity ?? this.malariaHeatmap.opacity,
            zIndex: layer.zIndex ?? (this.malariaHeatmap.zIndex + layerIndex),
            points: layer.points,
            radius: layer.radius ?? this.malariaHeatmap.radius,
            blur: layer.blur ?? this.malariaHeatmap.blur,
            gradient: layer.gradient
          }));
      }
      if (!this.malariaHeatmap.points || !this.malariaHeatmap.points.length) {
        return [];
      }
      return [{
        id: `malaria-heatmap-${this.index}`,
        visible: true,
        opacity: this.malariaHeatmap.opacity,
        zIndex: this.malariaHeatmap.zIndex,
        points: this.malariaHeatmap.points,
        radius: this.malariaHeatmap.radius,
        blur: this.malariaHeatmap.blur,
        gradient: this.malariaHeatmap.gradient
      }];
    },
    malariaDetectionOverlayItems() {
      if (!this.malariaDetectionOverlay) {
        return [];
      }
      if (Array.isArray(this.malariaDetectionOverlay.annotations) && this.malariaDetectionOverlay.annotations.length) {
        return this.malariaDetectionOverlay.annotations;
      }
      return this.malariaDetectionOverlay.points || [];
    }
  },
  methods: {
    async getMap() {
      await this.$refs.map.$createPromise;
      return this.$refs.map.$map;
    },
    async getViewComponent() {
      await this.$refs.view.$createPromise;
      return this.$refs.view;
    },
    async getBaseLayer() {
      await this.$refs.baseLayer.$createPromise;
      return this.$refs.baseLayer.$layer;
    },
    updateSize() {
      if (this.$refs.map) {
        this.$refs.map.updateSize();
      }
    }
  }
};
</script>
